import {
  keepPreviousData, useMutation, useQuery, useQueryClient
} from '@tanstack/react-query';

import APIGateway from '@/api';
import {
  DetailMetricsInfo,
  DetailRunInfo,
  Evaluation,
  EvaluationCreateReq,
  EvaluationsGetParams,
  EvaluationsGetRes,
  EvaluationUpdateReq,
  EvaluationWithDetails,
  Example,
  ExampleMetricDataMap,
  MetricDataMap,
  RunCreateReq,
  RunWithDetails,
  ScheduleCreateReq,
  SchedulesGetParams,
  SchedulesGetRes,
  ScheduleWithDetails,
  Source
} from '@/types/evals';


export function useEvaluationsGet(
  params?: EvaluationsGetParams,
  options?: { enabled?: boolean; refetchInterval?: number | false }
) {
  return useQuery<EvaluationsGetRes>( {
    refetchOnWindowFocus: false,
    queryKey: evaluationsKeys.search( params ?? {} ),
    queryFn: async() => {
      return await APIGateway.evals.evaluationsGet( params );
    },
    placeholderData: keepPreviousData,
    enabled: options?.enabled ?? true,
    ...( options?.refetchInterval !== undefined ? { refetchInterval: options.refetchInterval } : {} )
  } );
}














export const evaluationsKeys = {
  all: [ 'evaluations' ] as const,
  search: ( params?: EvaluationsGetParams ) => [ ...evaluationsKeys.all, 'search', params ] as const,
  runs: ( evalId: string, includeDetails?: boolean ) => [ ...evaluationsKeys.all, 'runs', evalId, { includeDetails } ] as const,
  runResults: ( evalId: string, runId: string ) => [ ...evaluationsKeys.all, 'runResults', evalId, runId ] as const,
  example: ( sourceId: string, version: number | 'latest', exampleId: string ) => [ ...evaluationsKeys.all, 'example', sourceId, version, exampleId ] as const,
  schedules: ( params?: SchedulesGetParams ) => [ ...evaluationsKeys.all, 'schedules', params ] as const
};



export function useCreateEvaluation() {
  const queryClient = useQueryClient();

  return useMutation( {
    mutationFn: async( args: EvaluationCreateReq ) => {
      return await APIGateway.evals.evaluationCreate( args );
    },
    onSuccess: () => {
      // Invalidate and refetch evaluations after successful creation
      queryClient.invalidateQueries( { queryKey: evaluationsKeys.all } );
    }
  } );
}

export function useSetDeprecation( evalId: string ) {
  const queryClient = useQueryClient();

  return useMutation( {
    mutationFn: async( args: EvaluationUpdateReq ) => {
      return await APIGateway.evals.evaluationUpdate( evalId, args );
    },
    onSuccess: () => {
      // Invalidate & refetch evals after successful deprecation change
      queryClient.invalidateQueries( { queryKey: evaluationsKeys.all } );
    }
  } );
}

export function useEvalRuns( evalId: string, includeDetails: boolean = true ) {
  return useQuery( {
    queryKey: evaluationsKeys.runs( evalId, includeDetails ),
    queryFn: async( { queryKey } ) => {
      if ( !evalId ) throw new Error( 'Evaluation ID required' );

      // Extract includeDetails from queryKey to ensure consistency
      const [ , , , options ] = queryKey;
      const shouldIncludeDetails = ( options as { includeDetails?: boolean } )?.includeDetails ?? true;

      // Fetch evaluation and runs separately (v2 API returns paginated runs, not aggregated)
      const [ evaluationRes, runsRes ] = await Promise.all( [
        APIGateway.evals.evaluationGet( evalId, { include_details: shouldIncludeDetails } ),
        APIGateway.evals.evaluationRunsList( evalId, { include_details: shouldIncludeDetails } )
      ] );

      if ( !evaluationRes || !runsRes.items ) {
        throw new Error( 'Unable to get evaluations or runs' );
      }

      return {
        evaluation: evaluationRes.evaluation,
        runs: runsRes.items.sort( ( a, b ) => b.run.created_at.localeCompare( a.run.created_at ) )
      };
    },
    enabled: !!evalId
  } );
}

export function useCreateRun( evalId: string ) {
  const queryClient = useQueryClient();

  return useMutation( {
    mutationFn: async( args: RunCreateReq ) => {
      if ( !evalId ) throw new Error( 'Evaluation ID required' );

      return await APIGateway.evals.evaluationRunCreate( evalId, args );
    },
    onSuccess: () => {
      // Invalidate runs query to refetch after creating a new run
      // Use the same includeDetails value as the query (default is true)
      queryClient.invalidateQueries( { queryKey: evaluationsKeys.runs( evalId, true ) } );
    }
  } );
}

export function useSchedulesList( params: SchedulesGetParams, options?: { enabled?: boolean } ) {
  return useQuery<{ items: ScheduleWithDetails[]; hasMore: boolean; nextCursor: string | null }>( {
    queryKey: evaluationsKeys.schedules( params ),
    queryFn: async() => {
      // Forward all params — including include_details, which the list endpoint needs
      // to populate each schedule's run stats (last_fire_at/last_run_id power "Last Run").
      const searchParams = params.cursor
        ? params
        : { sort_by: 'created_at', sort_order: 'desc' as const, ...params };
      const res: SchedulesGetRes = await APIGateway.evals.schedulesGet( searchParams );
      return { items: res.items, hasMore: res.pagination.has_more, nextCursor: res.pagination.next_cursor };
    },
    enabled: options?.enabled ?? true,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false
  } );
}

export function useCreateSchedule( evalId: string ) {
  const queryClient = useQueryClient();

  return useMutation( {
    mutationFn: async( args: ScheduleCreateReq ) => {
      if ( !evalId ) throw new Error( 'Evaluation ID required' );
      return await APIGateway.evals.scheduleCreate( args );
    },
    onSuccess: () => {
      queryClient.invalidateQueries( { queryKey: evaluationsKeys.runs( evalId, true ) } );
      queryClient.invalidateQueries( { queryKey: [ ...evaluationsKeys.all, 'schedules' ] } );
    }
  } );
}

// Pause/resume invalidate the 'schedules' key prefix (matches every cached
// schedules query regardless of params) so the row updates in place.
export function usePauseSchedule() {
  const queryClient = useQueryClient();

  return useMutation( {
    mutationFn: async( scheduleId: string ) => {
      return await APIGateway.evals.schedulePause( scheduleId );
    },
    onSuccess: () => {
      queryClient.invalidateQueries( { queryKey: [ ...evaluationsKeys.all, 'schedules' ] } );
    }
  } );
}

export function useResumeSchedule() {
  const queryClient = useQueryClient();

  return useMutation( {
    mutationFn: async( scheduleId: string ) => {
      return await APIGateway.evals.scheduleResume( scheduleId );
    },
    onSuccess: () => {
      queryClient.invalidateQueries( { queryKey: [ ...evaluationsKeys.all, 'schedules' ] } );
    }
  } );
}

// Delete deliberately does NOT invalidate here. The deleted schedule's row hosts
// the DeleteModal, so an immediate refetch would drop the row and unmount the modal
// while its success step is still showing. The caller (ScheduleActionMenu) invalidates
// on modal close instead, keeping the row mounted until the user dismisses the success.
export function useDeleteSchedule() {
  return useMutation( {
    mutationFn: async( scheduleId: string ) => {
      return await APIGateway.evals.scheduleDelete( scheduleId );
    }
  } );
}

type EvaluationRunResultsData = {
  evaluation: Evaluation;
  run: RunWithDetails<DetailMetricsInfo>;
  metricDataMap: MetricDataMap;
  metricKeys: string[];
  exampleMetricDataMap: ExampleMetricDataMap;
  source?: Source;
  sourceName?: string;
  metricProviderNames: Record<string, string>;
  metricProviderVersions: Record<string, Record<string, number>>;
};

export function useExampleQuery( sourceId: string, version: number | 'latest', exampleId: string ) {
  return useQuery<Example | null>( {
    queryKey: evaluationsKeys.example( sourceId, version, exampleId ),
    queryFn: async() => {
      const examples = await APIGateway.evals.exampleGet( sourceId, version, exampleId );
      return examples[ 0 ] ?? null;
    },
    enabled: !!sourceId && !!exampleId
  } );
}

export function useEvaluationRunResults( evaluationId: string, runId: string ) {
  return useQuery<EvaluationRunResultsData>( {
    queryKey: evaluationsKeys.runResults( evaluationId, runId ),
    queryFn: async() => {
      if ( !evaluationId || !runId ) {
        throw new Error( 'Evaluation ID and Run ID are required' );
      }

      // Fetch evaluation, run, and results in parallel
      const [ evaluationRes, run, resultsRes ] = await Promise.all( [
        APIGateway.evals.evaluationGet( evaluationId, { include_details: true } ),
        APIGateway.evals.runGet( runId ),
        APIGateway.evals.runResultsGet( runId )
      ] );

      const evaluation = evaluationRes.evaluation;
      const results = resultsRes;

      // Map metric data
      const metrics = run.details?.metrics || [];
      const metricDataMap = metrics.reduce<MetricDataMap>( ( acc, metric ) => {
        const key = `${ metric.metric_provider_id }|${ metric.metric_name }`;
        acc[ key ] = metric;
        return acc;
      }, {} );
      const metricKeys = Object.keys( metricDataMap ).sort();

      // Map example metric data
      const exampleMetricDataMap = results.reduce<ExampleMetricDataMap>( ( acc, result ) => {
        const { example_id, metric_provider_id, metric_name } = result;
        if ( !acc[ example_id ] ) acc[ example_id ] = {};
        acc[ example_id ][ `${ metric_provider_id }|${ metric_name }` ] = result;
        return acc;
      }, {} );

      // Fetch source data if applicable
      const shouldFetchSource = run.run.source_id && run.run.source_version;
      const sourceData = shouldFetchSource
        ? await APIGateway.evals.sourceGet( run.run.source_id!, run.run.source_version! ).catch( e => {
            console.error( 'Failed to fetch source:', e );
            return undefined;
          } )
        : undefined;

      // Fetch metric provider data
      const uniqueProviderIds = evaluation.metric_providers.map( mp => mp.metric_provider_id );
      const providers = await Promise.all(
        uniqueProviderIds.map( id => APIGateway.evals.metricProviderGet( id ).then( res => ( {
          id,
          name: res.name,
          versions: res.versions
        } ) ) )
      ).catch( e => {
        console.error( 'Failed to fetch metric providers:', e );
        return [];
      } );

      const metricProviderNames = providers.reduce<Record<string, string>>( ( acc, p ) => {
        acc[ p.id ] = p.name;
        return acc;
      }, {} );

      const metricProviderVersions = providers.reduce<Record<string, Record<string, number>>>( ( acc, p ) => {
        acc[ p.id ] = p.versions.reduce<Record<string, number>>( ( vAcc, v ) => {
          vAcc[ v.id ] = v.version;
          return vAcc;
        }, {} );
        return acc;
      }, {} );

      return {
        evaluation,
        run,
        metricDataMap,
        metricKeys,
        exampleMetricDataMap,
        source: sourceData,
        sourceName: sourceData?.name,
        metricProviderNames,
        metricProviderVersions
      };
    },
    enabled: !!evaluationId && !!runId
  } );
}
